import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/pro-regular-svg-icons";
import { faXmark } from "@fortawesome/pro-solid-svg-icons";
import { Button } from "@/shared/components/ui/Button";
import { IconButton } from "@/shared/components/ui/IconButton";
import Strings from "@/shared/utils/Strings.constants";
import { cn } from "@/shared/utils";

const positionClasses = "fixed inset-x-4 bottom-4 z-[45] rounded-xl xs:inset-x-auto xs:right-4";
interface MultiPlanNudgeToastProps {
    onDismiss: () => void;
    onAccept: () => void;
}

export const MultiPlanNudgeToast: React.FC<MultiPlanNudgeToastProps> = ({ onDismiss, onAccept }) => {
    return (
        // Entrance slides up only — deliberately no opacity in the animation. The toast must
        // be readable whether or not the animation ever runs (a framer-motion
        // `initial={{ opacity: 0 }}` previously left it invisible until a scroll forced a
        // repaint, and a fading CSS animation has the same failure mode while paused). With
        // transform-only, the worst case is a 16px offset rather than an invisible nudge.
        <div
            data-testid="multi-plan-nudge-toast"
            role="status"
            aria-live="polite"
            className={cn(positionClasses, "bg-background-brand-b-secondary p-4 shadow-lg duration-300 animate-in slide-in-from-bottom-4 fill-mode-forwards xs:w-[360px]")}
        >
            <p className="text-base font-bold leading-6 text-content-primary">{Strings.MULTI_PLAN_GUIDE.NUDGE_TITLE}</p>
            <p className="mt-1 text-sm leading-5 text-content-secondary">{Strings.MULTI_PLAN_GUIDE.NUDGE_BODY}</p>
            <div className="mt-3 flex items-center justify-end gap-2">
                <Button variant="ghost" size="sm" className="text-sm hover:bg-background-transparent hover:underline" data-testid="multi-plan-nudge-dismiss" onClick={onDismiss}>
                    {Strings.MULTI_PLAN_GUIDE.NUDGE_DISMISS}
                </Button>
                <Button variant="default-rounded" className="text-sm" size="sm" data-testid="multi-plan-nudge-cta" onClick={onAccept}>
                    {Strings.MULTI_PLAN_GUIDE.NUDGE_CTA}
                </Button>
            </div>
        </div>
    );
};

MultiPlanNudgeToast.displayName = "MultiPlanNudgeToast";

interface MultiPlanSuccessToastProps {
    onClose: () => void;
    /** how many plans were applied — one plan (incl. a single plan applied to every pet) reads singular */
    appliedPlanCount: number;
}

export const MultiPlanSuccessToast: React.FC<MultiPlanSuccessToastProps> = ({ onClose, appliedPlanCount }) => {
    const message = appliedPlanCount > 1 ? Strings.MULTI_PLAN_GUIDE.SUCCESS_TOAST_MULTIPLE : Strings.MULTI_PLAN_GUIDE.SUCCESS_TOAST_SINGLE;

    return (
        <div
            data-testid="multi-plan-success-toast"
            role="status"
            aria-live="polite"
            className={cn(positionClasses, "flex items-center justify-between gap-2 bg-background-success p-4 shadow-lg sm:w-auto sm:max-w-[400px]")}
        >
            <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faCircleCheck} className="size-4 shrink-0 text-content-success" />
                <p className="text-sm font-bold leading-5 text-content-success">{message}</p>
            </div>
            <IconButton onClick={onClose} aria-label={Strings.CLOSE_NOTIFICATION}>
                <FontAwesomeIcon icon={faXmark} className="size-4 text-content-primary" />
            </IconButton>
        </div>
    );
};

MultiPlanSuccessToast.displayName = "MultiPlanSuccessToast";
